import { getCurrentUser as amplifyGetCurrentUser, signOut } from 'aws-amplify/auth';
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ApiSystem, User } from "../state/types";
import { normalizeSystemsData } from '../components/system/systemHelpers';
import { userHasAccess } from '../services/RoleBasedAccess';
import { useSystemsApi } from '../api/useSystemsApi';
import { useAccountApi } from '../api/useAccountApi';
import { getRawAuthToken, setUnauthorizedHandler, SESSION_EXPIRED_MESSAGE } from '../api/apiClient';
import { stripUnitSuffix } from '../utils/systemCode';
import {
  LOGOUT_MESSAGE_KEY,
  RELEASE_ID,
  RELEASE_STORAGE_KEY,
  RELEASE_VERSION,
  RELEASED_AT,
  formatReleaseDate,
} from '../config/release';

const PUBLIC_PATHS = ['/login', '/logout', '/resetPassword'];

type InitializeAppOptions = {
  forceRefetch?: boolean;
  persistReleaseIdOnSuccess?: boolean;
  skipReleaseValidation?: boolean;
};

export const useSessionState = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [originalUser, setOriginalUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('originalUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [systems, setSystems] = useState<ApiSystem[]>(() => {
    const saved = localStorage.getItem('systems');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedSystem, setSelectedSystem] = useState<ApiSystem | null>(null);
  const [assetFilterResetToken, setAssetFilterResetToken] = useState(0);
  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;
  const setIsLoading = useCallback((loading: boolean) => {
    setLoadingCount((current) => {
      if (loading) return current + 1;
      return Math.max(0, current - 1);
    });
  }, []);

  const { getSystemsApi } = useSystemsApi();
  const { loginUserApi } = useAccountApi();
  const selectedSystemRef = useRef<ApiSystem | null>(null);
  const userRef = useRef<User | null>(user);
  const systemsRef = useRef<ApiSystem[]>(systems);

  const handleLogoutCleanup = useCallback(() => {
    setUser(null);
    setOriginalUser(null);
    setSystems([]);
    setSelectedSystem(null);
    localStorage.removeItem('user');
    localStorage.removeItem('systems');
    localStorage.removeItem('originalUser');
    localStorage.removeItem('impersonatedUser');
    localStorage.removeItem('assetsNavState');
    localStorage.removeItem('vehiclesNavState');
    localStorage.removeItem(RELEASE_STORAGE_KEY);
  }, []);

  const persistReleaseId = useCallback(() => {
    if (!RELEASE_ID) return;
    localStorage.setItem(RELEASE_STORAGE_KEY, RELEASE_ID);
  }, []);

  const logoutWithReason = useCallback(async (reason: string) => {
    sessionStorage.setItem(LOGOUT_MESSAGE_KEY, reason);
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out: ', error);
    } finally {
      handleLogoutCleanup();
      navigate('/login', { replace: true });
    }
  }, [handleLogoutCleanup, navigate]);

  const logoutForReleaseChange = useCallback(async () => {
    const releaseDetails = RELEASED_AT
      ? `version ${RELEASE_VERSION}, released ${formatReleaseDate(RELEASED_AT)}`
      : `version ${RELEASE_VERSION}`;

    await logoutWithReason(
      `The app was updated to ${releaseDetails}. Please sign in again to load the latest release.`
    );
  }, [logoutWithReason]);

  const initializeApp = useCallback(async ({
    forceRefetch = false,
    persistReleaseIdOnSuccess = false,
    skipReleaseValidation = false,
  }: InitializeAppOptions = {}) => {
    const isIframePath = location.pathname.toLowerCase().includes('/iframe');

    setIsLoading(true);
    const hasStaleUser = !!localStorage.getItem('user');
    const isMasquerading = !!localStorage.getItem('originalUser');

    try {
      if (isIframePath) {
        return;
      }

      let currentUser: User | null = userRef.current;

      // --- IDENTITY PHASE ---
      if (isMasquerading) {
        // Trust storage for Masquerade; skip API to avoid reverting to Admin data
        const storedImpersonated = localStorage.getItem('impersonatedUser');
        if (storedImpersonated) currentUser = JSON.parse(storedImpersonated);
        await amplifyGetCurrentUser(); // Ensure underlying admin session is alive
      } else {
        // Normal flow: only call API if we don't have a user, or in background
        if (!hasStaleUser || forceRefetch) {
          await amplifyGetCurrentUser();
          currentUser = await loginUserApi();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        }
      }

      const storedReleaseId = localStorage.getItem(RELEASE_STORAGE_KEY);
      if (
        currentUser &&
        RELEASE_ID &&
        !skipReleaseValidation &&
        storedReleaseId !== RELEASE_ID
      ) {
        await logoutForReleaseChange();
        return;
      }

      if (currentUser && RELEASE_ID && persistReleaseIdOnSuccess) {
        persistReleaseId();
      }

      // --- SYSTEMS PHASE ---
      if (currentUser) {
        let finalSystems: ApiSystem[] = [];

        // If we already have systems in state AND we aren't switching modes, skip API
        // Otherwise, fetch fresh to ensure we have the full list to filter from
        console.log("getting systems?")
        if (systemsRef.current.length > 0 && !isMasquerading && !forceRefetch) {
          finalSystems = systemsRef.current;
        } else {
          console.log("getting systems!")
          const rawSystems = await getSystemsApi();
          finalSystems = normalizeSystemsData(rawSystems);
        }

        // Apply masquerade filtering if necessary
        if (isMasquerading && !userHasAccess(currentUser.role, ['Internal'])) {
          const accessibleCodes = new Set((currentUser.system_access || []).map((code: string) => stripUnitSuffix(code)));
          finalSystems = finalSystems.filter(s =>
            accessibleCodes.has(s.systemCode)
          );
        }

        setSystems(finalSystems);
        localStorage.setItem('systems', JSON.stringify(finalSystems));

        // --- SELECTION PHASE ---
        const storageKey = `selectedSystem:${currentUser.username}`;
        const savedCode = stripUnitSuffix(localStorage.getItem(storageKey));
        const currentCode = stripUnitSuffix(selectedSystemRef.current?.systemCode || "");

        const next =
          finalSystems.find(s => s.systemCode === currentCode) ||
          finalSystems.find(s => s.systemCode === savedCode) ||
          finalSystems[0] ||
          null;

        setSelectedSystem(next);
        if (next) localStorage.setItem(storageKey, next.systemCode);
      }
    } catch (error) {
      console.error("Critical Boot Error:", error);
      handleLogoutCleanup();
    } finally {
      setIsLoading(false);
    }
  }, [getSystemsApi, handleLogoutCleanup, location.pathname, loginUserApi, logoutForReleaseChange, persistReleaseId, setIsLoading]);

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  // Register a global handler the apiClient can call when it detects a dead
  // session (e.g. the 401 retry also fails). We tear it down on unmount so
  // tests and HMR don't leak handlers across renders.
  useEffect(() => {
    setUnauthorizedHandler((reason) => {
      logoutWithReason(reason);
    });
    return () => setUnauthorizedHandler(null);
  }, [logoutWithReason]);

  // Proactive stale-session probe: when the tab regains focus after being
  // hidden, force-refresh the Cognito session via getRawAuthToken. We use
  // getRawAuthToken rather than fetchAuthSession directly because Amplify v6
  // returns an empty session object (no throw) when tokens are missing — we
  // need the helper's "No idToken available" throw to detect that case along
  // with refresh-token failures. If it throws, the user would hit a 401 on
  // their next click anyway — log them out now with a friendly message.
  useEffect(() => {
    const probe = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!userRef.current) return;
      const isPublicPath = PUBLIC_PATHS.some(path => location.pathname.startsWith(path));
      const isIframePath = location.pathname.toLowerCase().includes('/iframe');
      if (isPublicPath || isIframePath) return;

      try {
        await getRawAuthToken();
      } catch (error) {
        console.warn('Visibility-change session probe failed; logging out.', error);
        await logoutWithReason(SESSION_EXPIRED_MESSAGE);
      }
    };

    document.addEventListener('visibilitychange', probe);
    return () => document.removeEventListener('visibilitychange', probe);
  }, [location.pathname, logoutWithReason]);

  useEffect(() => {
    selectedSystemRef.current = selectedSystem;
  }, [selectedSystem]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    systemsRef.current = systems;
  }, [systems]);

  useEffect(() => {
    if (isLoading) return;

    const isPublicPath = PUBLIC_PATHS.some(path => location.pathname.startsWith(path));
    const isIframePath = location.pathname.toLowerCase().includes('iframe');

    if (!user && !isPublicPath && !isIframePath) {
      navigate('/login');
    }
  }, [user, isLoading, navigate, location.pathname]);

  const logoutUser = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out: ', error);
    } finally {
      handleLogoutCleanup();
      navigate('/login', { replace: true });
    }
  };

  const startMasquerade = (impersonatedUser: User) => {
    if (user && userHasAccess(user?.role, ['Admin', 'Support'])) {
      setOriginalUser(user);
      setUser(impersonatedUser);
      localStorage.setItem('user', JSON.stringify(impersonatedUser));
      localStorage.setItem('originalUser', JSON.stringify(user));
      localStorage.setItem('impersonatedUser', JSON.stringify(impersonatedUser));

      initializeApp({ forceRefetch: true });
    }
  };

  const stopMasquerade = () => {
    setUser(originalUser);
    setOriginalUser(null);
    localStorage.removeItem('originalUser');
    localStorage.removeItem('impersonatedUser');

    initializeApp({ forceRefetch: true });
  };

  const clearAssetNavStateAndNavigate = useCallback((path: string) => {
    console.log("clearing nav state")
    localStorage.removeItem('assetsNavState');
    navigate(path);
  }, [navigate]);

  const selectSystem = useCallback((sys: ApiSystem | null, options?: { skipNavigation?: boolean }) => {
    setSelectedSystem(sys);
    localStorage.removeItem('assetsNavState');
    localStorage.removeItem('vehiclesNavState');
    setAssetFilterResetToken((t) => t + 1);
    if (!user) return;
    const key = `selectedSystem:${user.username}`;
    if (sys) localStorage.setItem(key, sys.systemCode);
    else localStorage.removeItem(key);
    if (!options?.skipNavigation && location.pathname.startsWith('/asset/')) {
      clearAssetNavStateAndNavigate('/assets');
    }
  }, [clearAssetNavStateAndNavigate, location.pathname, user]);

  const loginUser = useCallback(() => initializeApp({
    forceRefetch: true,
    persistReleaseIdOnSuccess: true,
    skipReleaseValidation: true,
  }), [initializeApp]);

  /**
   * Refresh only the systems list from the API — does NOT re-verify Cognito
   * identity or re-fetch the user. Use this when the user is already
   * authenticated and you just want to pick up system changes (e.g., after
   * creating/editing/deleting a system, or when navigating to the Systems page).
   *
   * For a full identity + systems refresh (e.g., after login), use loginUser.
   */
  const refetchSystems = useCallback(async () => {
    if (!userRef.current) return;
    setIsLoading(true);
    try {
      const rawSystems = await getSystemsApi();
      const finalSystems = normalizeSystemsData(rawSystems);
      setSystems(finalSystems);
      localStorage.setItem('systems', JSON.stringify(finalSystems));

      // Re-apply selection logic in case the currently selected system was removed
      const storageKey = `selectedSystem:${userRef.current.username}`;
      const savedCode = stripUnitSuffix(localStorage.getItem(storageKey));
      const currentCode = stripUnitSuffix(selectedSystemRef.current?.systemCode || "");

      const next =
        finalSystems.find(s => s.systemCode === currentCode) ||
        finalSystems.find(s => s.systemCode === savedCode) ||
        finalSystems[0] ||
        null;

      setSelectedSystem(next);
      if (next) localStorage.setItem(storageKey, next.systemCode);
    } catch (error) {
      console.error("refetchSystems error:", error);
      // Don't logout on a failed systems refresh — just log the error.
      // The user still has cached systems and a valid session.
    } finally {
      setIsLoading(false);
    }
  }, [getSystemsApi, setIsLoading]);

  return {
    // User Session
    user,
    originalUser,
    isLoggedIn: !!user,
    isLoading,
    setIsLoading,
    loginUser,
    logoutUser,
    startMasquerade,
    stopMasquerade,
    handleNavigation: clearAssetNavStateAndNavigate,

    // Systems storage
    systems,
    setSystems,
    selectedSystem,
    selectSystem,
    refetchSystems,

    // Asset filter reset signal — bumped on user-initiated system switch
    assetFilterResetToken,
  };
};
